import React, { useMemo, useState, useRef, useLayoutEffect } from "react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, CheckCircle2, ShieldCheck, Clock3, BadgeCheck, FileText, Hammer } from "lucide-react";
const SERVICE_OPTIONS = [{
  value: "asbestverwijdering",
  icon: Hammer,
  title: "Asbestverwijdering",
  description: "Voor het veilig laten verwijderen van asbest",
  hint: "Bij renovatie of sanering",
  topLabel: "Sanering"
}, {
  value: "asbestattest",
  icon: FileText,
  title: "Asbestattest",
  description: "Voor een attest bij verkoop van een gebouw",
  hint: "Verplicht bij verkoop",
  topLabel: "Verkoop"
}];
const STEP_2_FIELDS = [{
  name: "name",
  type: "text",
  placeholder: "Naam",
  className: "",
  rules: {
    required: "Naam is verplicht",
    minLength: {
      value: 2,
      message: "Naam moet minstens 2 karakters bevatten"
    }
  }
}, {
  name: "email",
  type: "email",
  placeholder: "E-mail",
  className: "sm:col-span-2",
  rules: {
    required: "E-mail is verplicht",
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Geef een geldig e-mailadres in"
    }
  }
}, {
  name: "phone",
  type: "tel",
  placeholder: "Telefoonnummer",
  className: "sm:col-span-2",
  rules: {
    required: "Telefoonnummer is verplicht",
    pattern: {
      value: /^[\+]?[0-9\s()+/-]{6,20}$/,
      message: "Geef een geldig telefoonnummer in"
    }
  }
}];
export default function SnelleBouwRenovatie() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="26704" id="vergelijk-asbest-offertes" className="dark bg-background text-foreground relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-black/55 z-10" />
        <div className="w-full h-full bg-cover bg-center bg-no-repeat bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604417/img/modern_interior_dining.png)]" />
      </div>

      <div ref={ref} className={`relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid xl:grid-cols-[1.05fr_0.95fr] gap-8 xl:gap-12 items-center">
          <div className="max-w-3xl text-left hidden xl:block">
            <div className="inline-flex items-center rounded-full bg-white/10 border border-white/15 px-4 py-2 mb-5 backdrop-blur-sm">
              <span className="text-white text-sm font-semibold">
                Gratis & vrijblijvend offertes aanvragen
              </span>
            </div>

            <h1 className="text-white text-4xl md:text-[58px] font-bold leading-[1.05] mb-5 font-serif max-w-3xl">
              Vraag gratis offertes aan voor asbestattest of asbestverwijdering
            </h1>

            <p className="text-white/95 text-lg md:text-[24px] font-normal leading-relaxed mb-8 font-sans max-w-2xl">
              Vergelijk snel offertes van asbestspecialisten en kies de oplossing
              die het best bij uw project past.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
              {["Gratis en vrijblijvend", "Snel meerdere offertes vergelijken", "Voor asbestattest en verwijdering", "Eenvoudig online aanvragen"].map(item => <div key={item} className="flex items-center gap-3 rounded-xl bg-white/10 border border-white/10 px-4 py-3 backdrop-blur-sm">
                  <CheckCircle2 className="size-5 text-[#53d87c] shrink-0" />
                  <span className="text-white text-sm md:text-base">{item}</span>
                </div>)}
            </div>
          </div>

          <div className="w-full flex justify-center xl:justify-end">
            <div className="w-full max-w-[520px] 2xl:max-w-[620px]">
              <div className="bg-white rounded-[28px] border border-white/20 shadow-2xl overflow-hidden">
                <div className="px-6 md:px-8 2xl:px-10 pt-7 md:pt-8 2xl:pt-10 pb-5 md:pb-6 border-b border-[#ECEFF1] text-center md:text-left">
                  <h2 className="text-[#182329] text-[34px] md:text-[42px] 2xl:text-[56px] font-bold leading-[1.05]">
                    Ontvang gratis offertes
                  </h2>
                  <p className="text-[#53626A] text-lg md:text-xl 2xl:text-2xl mt-4 leading-relaxed">
                    Vul hieronder uw gegevens in en start uw aanvraag.
                  </p>
                </div>

                <div className="p-6 md:p-8 2xl:p-10">
                  <FormProvider formId="asbest-offerte-hero" sectionName="SnelleBouwRenovatie" successMessage="Bedankt! Uw aanvraag is succesvol verzonden." defaultValues={{
                  service_type: "",
                  postcode: "",
                  street: "",
                  house_number: "",
                  name: "",
                  email: "",
                  phone: ""
                }}>
                    <LeadFormContent />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function LeadFormContent() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage,
    submitError
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const stepTwoTopRef = useRef<HTMLDivElement | null>(null);
  const stepOneRules = useMemo(() => ({
    service_type: {
      required: "Kies waarvoor u offertes wilt ontvangen"
    },
    postcode: {
      required: "Postcode is verplicht",
      minLength: {
        value: 4,
        message: "Postcode moet minstens 4 cijfers bevatten"
      },
      pattern: {
        value: /^[0-9]{4,6}$/,
        message: "Geef een geldige postcode in"
      }
    },
    street: {
      required: "Straat is verplicht",
      minLength: {
        value: 2,
        message: "Straat moet minstens 2 karakters bevatten"
      }
    },
    house_number: {
      required: "Huisnummer is verplicht"
    }
  }), []);
  useLayoutEffect(() => {
    if (step === 2) {
      stepTwoTopRef.current?.scrollIntoView({
        behavior: "auto",
        block: "start"
      });
    }
  }, [step]);
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await handleSubmit();
    } catch (error) {
      console.error("Form submission failed:", error);
    }
  };
  const goToStepTwo = () => {
    setStep(2);
  };
  if (isSubmitted && !submitError) {
    return <div className="py-8 text-center">
        <div className="mx-auto mb-4 flex items-center justify-center size-16 rounded-full bg-[#EAF9EF]">
          <CheckCircle2 className="size-8 text-[#1AAD4A]" />
        </div>
        <p className="text-[#1AAD4A] font-bold text-xl">{successMessage}</p>
        <p className="text-[#53626A] text-base mt-2">
          We hebben uw aanvraag goed ontvangen. U wordt zo snel mogelijk gecontacteerd.
        </p>
      </div>;
  }
  return <Form onSubmit={handleFormSubmit}>
      <div className="flex items-center gap-4 mb-8">
        <div className={`flex items-center justify-center size-12 rounded-full text-lg font-bold ${step >= 1 ? "bg-[#1AAD4A] text-white" : "bg-[#EDF2F4] text-[#53626A]"}`}>
          1
        </div>
        <div className={`h-[3px] flex-1 ${step >= 2 ? "bg-[#1AAD4A]" : "bg-[#EDF2F4]"}`} />
        <div className={`flex items-center justify-center size-12 rounded-full text-lg font-bold ${step >= 2 ? "bg-[#1AAD4A] text-white" : "bg-[#EDF2F4] text-[#53626A]"}`}>
          2
        </div>
      </div>

      {step === 1 && <div className="space-y-6">
          <div className="text-center">
            <p className="text-[#182329] font-sans font-bold mb-3 text-[26px] md:text-[30px] 2xl:text-[32px] leading-tight">
              Ik wens een:
            </p>
            <p className="text-[#53626A] text-base md:text-lg">
              Kies waarvoor u offertes wilt ontvangen
            </p>
          </div>

          <FormField control={control} name="service_type" rules={stepOneRules.service_type} render={({
        field
      }) => <FormItem>
                <FormControl>
                  <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                    {SERVICE_OPTIONS.map(option => {
              const isActive = field.value === option.value;
              const Icon = option.icon;
              return <label key={option.value} className={`relative cursor-pointer rounded-3xl border p-5 md:p-5 2xl:p-6 transition-all duration-200 text-left min-h-[170px] md:min-h-[185px] 2xl:min-h-[210px] flex flex-col justify-between ${isActive ? "border-[#1AAD4A] bg-[#F3FCF6] shadow-xl ring-4 ring-[#1AAD4A]/20 scale-[1.01]" : "border-[#D8DEE1] bg-white hover:border-[#1AAD4A]/70 hover:shadow-lg"}`}>
                          <input type="radio" {...field} value={option.value} checked={isActive} onChange={() => field.onChange(option.value)} className="sr-only" />

                          <div className="flex items-start justify-between gap-4">
                            <div className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs md:text-sm font-semibold ${isActive ? "bg-[#1AAD4A] text-white" : "bg-[#F4F7F8] text-[#53626A]"}`}>
                              {option.topLabel}
                            </div>

                            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 text-sm font-bold transition-all ${isActive ? "border-[#1AAD4A] bg-[#1AAD4A] text-white shadow-md" : "border-[#C9D1D5] bg-white text-transparent"}`}>
                              ✓
                            </div>
                          </div>

                          <div className="mt-4">
                            <div className="flex items-center gap-3">
                              <div className={`flex items-center justify-center w-12 h-12 md:w-13 md:h-13 2xl:w-14 2xl:h-14 rounded-2xl shrink-0 ${isActive ? "bg-[#DDF6E5]" : "bg-[#EEF3F4]"}`}>
                                <Icon className={`size-6 md:size-7 ${isActive ? "text-[#1AAD4A]" : "text-[#53626A]"}`} />
                              </div>

                              <div className="text-[#182329] font-bold text-[20px] md:text-[22px] 2xl:text-[28px] leading-tight break-words">
                                {option.title}
                              </div>
                            </div>

                            <p className="mt-3 text-[#53626A] text-sm md:text-base leading-relaxed">
                              {option.description}
                            </p>
                          </div>

                          <div className={`mt-4 inline-flex w-fit items-center rounded-full px-4 py-2 text-xs md:text-sm 2xl:text-base font-medium leading-tight ${isActive ? "bg-[#1AAD4A] text-white" : "bg-[#F4F7F8] text-[#53626A]"}`}>
                            {option.hint}
                          </div>

                          {isActive && <div className="absolute inset-x-0 bottom-0 h-1.5 bg-[#1AAD4A] rounded-b-3xl" />}
                        </label>;
            })}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField control={control} name="postcode" rules={stepOneRules.postcode} render={({
          field
        }) => <FormItem className="sm:col-span-1">
                  <FormControl>
                    <input {...field} type="text" inputMode="numeric" placeholder="Postcode" className="bg-white text-[#182329] border border-[#DCDFE0] rounded-2xl h-16 md:h-[72px] px-5 text-xl md:text-2xl focus:outline-none focus:ring-2 focus:ring-[#1AAD4A]/20 focus:border-[#1AAD4A] w-full font-sans transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="street" rules={stepOneRules.street} render={({
          field
        }) => <FormItem className="sm:col-span-2">
                  <FormControl>
                    <input {...field} type="text" placeholder="Straat" className="bg-white text-[#182329] border border-[#DCDFE0] rounded-2xl h-16 md:h-[72px] px-5 text-xl md:text-2xl focus:outline-none focus:ring-2 focus:ring-[#1AAD4A]/20 focus:border-[#1AAD4A] w-full font-sans transition-all duration-200" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="house_number" rules={stepOneRules.house_number} render={({
        field
      }) => <FormItem>
                <FormControl>
                  <input {...field} type="text" placeholder="Huisnummer" className="bg-white text-[#182329] border border-[#DCDFE0] rounded-2xl h-16 md:h-[72px] px-5 text-xl md:text-2xl focus:outline-none focus:ring-2 focus:ring-[#1AAD4A]/20 focus:border-[#1AAD4A] w-full font-sans transition-all duration-200" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="grid gap-3">
            <div className="rounded-2xl bg-[#F7FAFB] border border-[#E7EEF0] px-5 py-4">
              <div className="flex items-start gap-3 text-[#53626A]">
                <ShieldCheck className="size-5 mt-0.5 shrink-0 text-[#1AAD4A]" />
                <span className="text-base md:text-lg">Gratis en vrijblijvend</span>
              </div>
            </div>

            <div className="rounded-2xl bg-[#F7FAFB] border border-[#E7EEF0] px-5 py-4">
              <div className="flex items-start gap-3 text-[#53626A]">
                <Clock3 className="size-5 mt-0.5 shrink-0 text-[#1AAD4A]" />
                <span className="text-base md:text-lg">In minder dan 1 minuut aangevraagd</span>
              </div>
            </div>

            <div className="rounded-2xl bg-[#F7FAFB] border border-[#E7EEF0] px-5 py-4">
              <div className="flex items-start gap-3 text-[#53626A]">
                <BadgeCheck className="size-5 mt-0.5 shrink-0 text-[#1AAD4A]" />
                <span className="text-base md:text-lg">Vergelijk eenvoudig meerdere offertes</span>
              </div>
            </div>
          </div>

          <Button type="button" onClick={goToStepTwo} className="w-full h-16 md:h-[72px] bg-[#1AAD4A] text-white hover:bg-[#16963f] rounded-2xl px-6 text-2xl font-bold border-none shadow-none">
            Volgende stap
          </Button>
        </div>}

      {step === 2 && <div className="space-y-6">
          <div ref={stepTwoTopRef} className="text-center">
            <p className="text-[#182329] font-sans font-bold mb-2 text-[30px] leading-tight">
              Uw contactgegevens
            </p>
            <p className="text-[#53626A] text-lg md:text-xl">
              Laat uw gegevens achter en ontvang gratis offertes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {STEP_2_FIELDS.map(input => <FormField key={input.name} control={control} name={input.name} rules={input.rules} render={({
          field
        }) => <FormItem className={input.className}>
                    <FormControl>
                      <input {...field} type={input.type} placeholder={input.placeholder} className="bg-white text-[#182329] border border-[#DCDFE0] rounded-2xl h-16 md:h-[72px] px-5 text-xl md:text-2xl focus:outline-none focus:ring-2 focus:ring-[#1AAD4A]/20 focus:border-[#1AAD4A] w-full font-sans transition-all duration-200" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />)}
          </div>

          <div className="rounded-2xl bg-[#F7FAFB] border border-[#E7EEF0] px-5 py-4">
            <div className="flex items-start gap-3 text-[#53626A]">
              <ShieldCheck className="size-5 mt-0.5 shrink-0 text-[#1AAD4A]" />
              <span className="text-sm md:text-base">
                Uw aanvraag is gratis en vrijblijvend. Uw gegevens worden vertrouwelijk behandeld.
              </span>
            </div>
          </div>

          {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
              <p className="text-red-800 text-base font-sans">{submitError}</p>
            </div>}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="button" variant="outline" onClick={() => setStep(1)} className="sm:w-auto w-full h-14 md:h-16 rounded-2xl border-[#DCDFE0] text-[#182329] text-lg">
              Vorige stap
            </Button>

            <Button type="submit" disabled={isSubmitting} className="w-full h-16 md:h-[72px] bg-[#1AAD4A] text-white hover:bg-[#16963f] rounded-2xl px-6 text-xl md:text-2xl font-bold border-none shadow-none flex items-center justify-center gap-2">
              {isSubmitting ? <Loader className="animate-spin size-5" /> : <span className="translate-x-[-2px] tracking-tight">
                  Ontvang 3 gratis offertes
                </span>}
            </Button>
          </div>

          <p className="text-sm text-[#6B7A81] leading-relaxed text-center">
            Door te verzenden gaat u akkoord dat uw aanvraag wordt doorgestuurd
            voor een offerte-opmaak.
          </p>
        </div>}
    </Form>;
}